import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getPasswordInfo, getPasswordForDate } from '../utils/passwordGenerator';

const ViewerContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgb(24, 29, 49) 0%, rgb(21, 10, 32) 100%);
  padding: 20px;
`;

const ViewerCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: #fff;
  font-size: 2rem;
  margin-bottom: 10px;
  font-weight: 300;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 30px;
  font-size: 1rem;
`;

const PasswordDisplay = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 2px solid rgba(0, 212, 255, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
`;

const PasswordText = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #00d4ff;
  letter-spacing: 4px;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
  margin-bottom: 10px;
`;

const DateText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const TimeText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const InfoSection = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const InfoTitle = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 15px;
  font-weight: 300;
`;

const InfoList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  line-height: 1.6;
`;

const InfoItem = styled.li`
  margin-bottom: 8px;
`;

const DateSelector = styled.div`
  margin: 20px 0;
`;

const DateInput = styled.input`
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  margin-right: 10px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  background: linear-gradient(45deg, #00d4ff, #0066cc);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
  }
`;

const PasswordViewer = () => {
  const [passwordInfo, setPasswordInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [customPassword, setCustomPassword] = useState('');

  useEffect(() => {
    // Получаем информацию о текущем пароле
    const info = getPasswordInfo();
    setPasswordInfo(info);
    
    // Устанавливаем сегодняшнюю дату как значение по умолчанию
    setSelectedDate(info.date);
  }, []);

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    
    if (date) {
      const password = getPasswordForDate(date);
      setCustomPassword(password);
    } else {
      setCustomPassword('');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Можно добавить уведомление о копировании
      alert('Пароль скопирован в буфер обмена!');
    });
  };

  if (!passwordInfo) {
    return (
      <ViewerContainer>
        <ViewerCard>
          <Title>Загрузка...</Title>
        </ViewerCard>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer>
      <ViewerCard>
        <Title>🔑 Генератор паролей</Title>
        <Subtitle>Пароли для доступа к сайту (действительны 15 дней)</Subtitle>
        
        <PasswordDisplay>
          <PasswordText onClick={() => copyToClipboard(passwordInfo.password)} style={{ cursor: 'pointer' }}>
            {passwordInfo.password}
          </PasswordText>
          <DateText>Период: {passwordInfo.periodStart} - {passwordInfo.periodEnd}</DateText>
          <TimeText>Осталось дней: {passwordInfo.daysRemaining} | Обновлено: {new Date(passwordInfo.timestamp).toLocaleString('ru-RU')}</TimeText>
        </PasswordDisplay>
        
        <DateSelector>
          <DateInput
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            max={passwordInfo.date} // Нельзя выбрать будущую дату
          />
          <Button onClick={() => copyToClipboard(customPassword)} disabled={!customPassword}>
            Копировать
          </Button>
        </DateSelector>
        
        {customPassword && selectedDate !== passwordInfo.date && (
          <PasswordDisplay>
            <PasswordText onClick={() => copyToClipboard(customPassword)} style={{ cursor: 'pointer' }}>
              {customPassword}
            </PasswordText>
            <DateText>Для даты: {selectedDate}</DateText>
          </PasswordDisplay>
        )}
        
        <InfoSection>
          <InfoTitle>ℹ️ Информация</InfoTitle>
          <InfoList>
            <InfoItem>Пароль генерируется на основе 15-дневных периодов</InfoItem>
            <InfoItem>Пароль действителен в течение 15 дней</InfoItem>
            <InfoItem>Нажмите на пароль, чтобы скопировать его</InfoItem>
            <InfoItem>Аутентификация сохраняется в браузере до конца периода</InfoItem>
            <InfoItem>Для входа используйте пароль в верхнем регистре</InfoItem>
          </InfoList>
        </InfoSection>
      </ViewerCard>
    </ViewerContainer>
  );
};

export default PasswordViewer;
